import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Users, 
  UserCheck, 
  Briefcase, 
  Brain, 
  TrendingUp, 
  RefreshCw,
  Eye
} from 'lucide-react';
import { API_BASE_URL } from './config';

const AdminPanel = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(7);
  const [dashboardData, setDashboardData] = useState(null);
  const [detailedData, setDetailedData] = useState({
    recruiters: null,
    jobseekers: null,
    jobs: null,
    llm: null,
    llmBreakdown: null,
    logins: null
  });
  const [expandedSections, setExpandedSections] = useState({
    overview: true,
    recruiters: false,
    jobseekers: false,
    jobs: false,
    llm: false,
    logins: false
  });
  const [activeDetailView, setActiveDetailView] = useState(null);
  const [activeLLMBreakdown, setActiveLLMBreakdown] = useState(null);

  const token = localStorage.getItem("token");
  const isInitialLoadRef = useRef(true);

  const fetchDashboardSummary = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/dashboard/summary?days=${days}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch dashboard data');
      }

      const data = await response.json();
      setDashboardData(data);
    } catch (err) {
      setError(err.message);
    }
  }, [days, token]);

  const fetchDetailedData = useCallback(async (type) => {
    try {
      let endpoint = '';
      switch (type) {
        case 'recruiters':
          endpoint = `/admin/stats/recruiters/daily?days=${days}`;
          break;
        case 'jobseekers':
          endpoint = `/admin/stats/jobseekers/daily?days=${days}`;
          break;
        case 'jobs':
          endpoint = `/admin/stats/jobs/daily?days=${days}`;
          break;
        case 'llm':
          endpoint = `/admin/stats/llm/daily?days=${days}`;
          break;
        case 'llmBreakdown':
          endpoint = `/admin/stats/llm/breakdown?days=${days}`;
          break;
        case 'logins':
          endpoint = `/admin/stats/logins/daily?days=${days}`;
          break;
        default:
          return;
      }

      // Add timeout to fetch request
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 30000); // 30 second timeout

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Failed to fetch ${type} data: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      setDetailedData(prev => ({ ...prev, [type]: data }));
    } catch (err) {
      // Handle fetch errors silently or with user-friendly messages if needed
    }
  }, [days, token]);

  const handleRefresh = async () => {
    setLoading(true);
    setError(null);
    await fetchDashboardSummary();
    setLoading(false);
  };

  const toggleSection = (section) => {
    const willBeExpanded = !expandedSections[section];
    
    setExpandedSections(prev => ({
      ...prev,
      [section]: willBeExpanded
    }));

    // Fetch detailed data when expanding a section (or if data is null due to time range change)
    if (willBeExpanded && !detailedData[section]) {
      fetchDetailedData(section);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Initial data load
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      await fetchDashboardSummary();
      setLoading(false);
      isInitialLoadRef.current = false;
    };

    loadInitialData();
  }, [fetchDashboardSummary]);

  // Handle time range changes
  useEffect(() => {
    const handleTimeRangeChange = async () => {
      // Don't run on initial load
      if (isInitialLoadRef.current) return;
      
      setLoading(true);
      
      // Clear all detailed data to force refetch with new time range
      setDetailedData({
        recruiters: null,
        jobseekers: null,
        jobs: null,
        llm: null,
        llmBreakdown: null,
        logins: null
      });
      
      // Refetch dashboard summary with new time range
      await fetchDashboardSummary();
      
      setLoading(false);
    };

    handleTimeRangeChange();
  }, [days, fetchDashboardSummary]);

  // Separate effect to handle refetching expanded sections when data is cleared
  useEffect(() => {
    // Only run if we have cleared data (detailedData values are null) but sections are expanded
    const shouldRefetch = Object.keys(expandedSections).some(key => 
      expandedSections[key] && 
      key !== 'overview' && 
      detailedData[key] === null
    );

    if (shouldRefetch && !loading) {
      const expandedSectionsList = Object.keys(expandedSections).filter(
        key => expandedSections[key] && key !== 'overview' && detailedData[key] === null
      );
      
      expandedSectionsList.forEach(async (section) => {
        await fetchDetailedData(section);
        
        // Also fetch LLM breakdown if LLM section is expanded
        if (section === 'llm') {
          await fetchDetailedData('llmBreakdown');
        }
      });
    }
  }, [expandedSections, detailedData, loading, fetchDetailedData]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        <h4 className="alert-heading">Error Loading Admin Data</h4>
        <p>{error}</p>
        <hr />
        <button className="btn btn-outline-danger" onClick={handleRefresh}>
          <RefreshCw size={16} className="me-2" />
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="h4 mb-1">Administration Dashboard</h2>
          <p className="text-muted mb-0">Monitor system usage and analytics</p>
        </div>
        <div className="d-flex align-items-center gap-3">
          <div className="d-flex align-items-center gap-2">
            <label className="form-label mb-0 small">Time Range:</label>
            <select 
              className="form-select form-select-sm" 
              value={days} 
              onChange={(e) => setDays(parseInt(e.target.value))}
              style={{ width: '120px' }}
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>
          <button className="btn btn-outline-primary btn-sm" onClick={handleRefresh}>
            <RefreshCw size={16} className="me-1" />
            Refresh
          </button>
        </div>
      </div>

      {/* Overview Cards */}
      {dashboardData && (
        <div className="row mb-4">
          <div className="col-md-6 col-lg-3 mb-3">
            <div className="card h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0">
                    <Users className="text-primary" size={24} />
                  </div>
                  <div className="ms-3">
                    <h6 className="card-title mb-0">Total Recruiters</h6>
                    <h4 className="mb-0">{dashboardData.summary.total_recruiters}</h4>
                    <small className="text-success">
                      +{dashboardData.summary.new_recruiters_last_n_days} last {days} days
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-6 col-lg-3 mb-3">
            <div className="card h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0">
                    <UserCheck className="text-success" size={24} />
                  </div>
                  <div className="ms-3">
                    <h6 className="card-title mb-0">Total Jobseekers</h6>
                    <h4 className="mb-0">{dashboardData.summary.total_jobseekers}</h4>
                    <small className="text-success">
                      +{dashboardData.summary.new_jobseekers_last_n_days} last {days} days
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-6 col-lg-3 mb-3">
            <div className="card h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0">
                    <Briefcase className="text-warning" size={24} />
                  </div>
                  <div className="ms-3">
                    <h6 className="card-title mb-0">New Job Postings</h6>
                    <h4 className="mb-0">{dashboardData.summary.new_jobs_last_n_days}</h4>
                    <small className="text-muted">Last {days} days</small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-6 col-lg-3 mb-3">
            <div className="card h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0">
                    <Brain className="text-info" size={24} />
                  </div>
                  <div className="ms-3">
                    <h6 className="card-title mb-0">LLM API Calls</h6>
                    <h4 className="mb-0">{dashboardData.summary.llm_calls_last_n_days}</h4>
                    <small className="text-muted">Last {days} days</small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-6 col-lg-3 mb-3">
            <div className="card h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0">
                    <TrendingUp className="text-purple" size={24} />
                  </div>
                  <div className="ms-3">
                    <h6 className="card-title mb-0">User Logins</h6>
                    <h4 className="mb-0">{dashboardData.summary.logins_last_n_days}</h4>
                    <small className="text-muted">Last {days} days</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detailed Analytics Sections */}
      <div className="accordion" id="adminAccordion">
        
        {/* Recruiters Section */}
        <div className="accordion-item">
          <h2 className="accordion-header">
            <button
              className={`accordion-button ${expandedSections.recruiters ? '' : 'collapsed'}`}
              type="button"
              onClick={() => toggleSection('recruiters')}
            >
              <Users size={20} className="me-2" />
              Recruiter Analytics
            </button>
          </h2>
          {expandedSections.recruiters && (
            <div className="accordion-collapse collapse show">
              <div className="accordion-body">
                {detailedData.recruiters ? (
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">Daily Recruiter Signups ({days} days)</h6>
                      <small className="text-muted">
                        Total: {detailedData.recruiters.total_new_recruiters} new recruiters
                      </small>
                    </div>
                    
                    {detailedData.recruiters.data.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-sm">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Count</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detailedData.recruiters.data.map((day) => (
                              <tr key={day.date}>
                                <td>{formatDate(day.date)}</td>
                                <td>
                                  <span className="badge bg-primary">{day.count}</span>
                                </td>
                                <td>
                                  {day.recruiters.length > 0 && (
                                    <button
                                      className="btn btn-sm btn-outline-secondary"
                                      onClick={() => setActiveDetailView({
                                        type: 'recruiters',
                                        date: day.date,
                                        data: day.recruiters
                                      })}
                                    >
                                      <Eye size={14} className="me-1" />
                                      View Details
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-muted">No new recruiters in the selected time period.</p>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-3">
                    <div className="spinner-border spinner-border-sm" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Jobseekers Section */}
        <div className="accordion-item">
          <h2 className="accordion-header">
            <button
              className={`accordion-button ${expandedSections.jobseekers ? '' : 'collapsed'}`}
              type="button"
              onClick={() => toggleSection('jobseekers')}
            >
              <UserCheck size={20} className="me-2" />
              Jobseeker Analytics
            </button>
          </h2>
          {expandedSections.jobseekers && (
            <div className="accordion-collapse collapse show">
              <div className="accordion-body">
                {detailedData.jobseekers ? (
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">Daily Jobseeker Signups ({days} days)</h6>
                      <small className="text-muted">
                        Total: {detailedData.jobseekers.total_new_jobseekers} new jobseekers
                      </small>
                    </div>
                    
                    {detailedData.jobseekers.data.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-sm">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Count</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detailedData.jobseekers.data.map((day) => (
                              <tr key={day.date}>
                                <td>{formatDate(day.date)}</td>
                                <td>
                                  <span className="badge bg-success">{day.count}</span>
                                </td>
                                <td>
                                  {day.jobseekers.length > 0 && (
                                    <button
                                      className="btn btn-sm btn-outline-secondary"
                                      onClick={() => setActiveDetailView({
                                        type: 'jobseekers',
                                        date: day.date,
                                        data: day.jobseekers
                                      })}
                                    >
                                      <Eye size={14} className="me-1" />
                                      View Details
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-muted">No new jobseekers in the selected time period.</p>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-3">
                    <div className="spinner-border spinner-border-sm" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Jobs Section */}
        <div className="accordion-item">
          <h2 className="accordion-header">
            <button
              className={`accordion-button ${expandedSections.jobs ? '' : 'collapsed'}`}
              type="button"
              onClick={() => toggleSection('jobs')}
            >
              <Briefcase size={20} className="me-2" />
              Job Posting Analytics
            </button>
          </h2>
          {expandedSections.jobs && (
            <div className="accordion-collapse collapse show">
              <div className="accordion-body">
                {detailedData.jobs ? (
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">Daily Job Postings ({days} days)</h6>
                      <small className="text-muted">
                        Total: {detailedData.jobs.total_new_jobs} new jobs
                      </small>
                    </div>

                    {/* Top Recruiters by Job Postings */}
                    {detailedData.jobs.recruiter_breakdown && detailedData.jobs.recruiter_breakdown.length > 0 && (
                      <div className="mb-4">
                        <h6 className="text-muted mb-2">Top Recruiters by Job Postings</h6>
                        <div className="table-responsive">
                          <table className="table table-sm">
                            <thead>
                              <tr>
                                <th>Recruiter</th>
                                <th>Email</th>
                                <th>Job Count</th>
                              </tr>
                            </thead>
                            <tbody>
                              {detailedData.jobs.recruiter_breakdown.slice(0, 5).map((recruiter) => (
                                <tr key={recruiter.recruiter_id}>
                                  <td>{recruiter.recruiter_name}</td>
                                  <td className="text-muted small">{recruiter.email}</td>
                                  <td>
                                    <span className="badge bg-warning text-dark">{recruiter.count}</span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    
                    {detailedData.jobs.daily_data.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-sm">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Count</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detailedData.jobs.daily_data.map((day) => (
                              <tr key={day.date}>
                                <td>{formatDate(day.date)}</td>
                                <td>
                                  <span className="badge bg-warning text-dark">{day.count}</span>
                                </td>
                                <td>
                                  {day.jobs.length > 0 && (
                                    <button
                                      className="btn btn-sm btn-outline-secondary"
                                      onClick={() => setActiveDetailView({
                                        type: 'jobs',
                                        date: day.date,
                                        data: day.jobs
                                      })}
                                    >
                                      <Eye size={14} className="me-1" />
                                      View Details
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-muted">No new job postings in the selected time period.</p>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-3">
                    <div className="spinner-border spinner-border-sm" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* LLM Analytics Section */}
        <div className="accordion-item">
          <h2 className="accordion-header">
            <button
              className={`accordion-button ${expandedSections.llm ? '' : 'collapsed'}`}
              type="button"
              onClick={() => {
                const willBeExpanded = !expandedSections.llm;
                toggleSection('llm');
                if (willBeExpanded && !detailedData.llmBreakdown) {
                  fetchDetailedData('llmBreakdown');
                }
              }}
            >
              <Brain size={20} className="me-2" />
              LLM Usage Analytics
            </button>
          </h2>
          {expandedSections.llm && (
            <div className="accordion-collapse collapse show">
              <div className="accordion-body">
                {detailedData.llm ? (
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">Daily LLM API Calls ({days} days)</h6>
                      <small className="text-muted">
                        Total: {detailedData.llm.total_calls} calls | {detailedData.llm.total_tokens.toLocaleString()} tokens
                      </small>
                    </div>

                    {/* Model Breakdown */}
                    {detailedData.llmBreakdown && (
                      <div className="row mb-4">
                        <div className="col-md-6">
                          <h6 className="text-muted mb-2">Model Usage Breakdown</h6>
                          {detailedData.llmBreakdown.model_breakdown.map((model) => (
                            <div key={model.model_name} className="d-flex justify-content-between align-items-center mb-2">
                              <span className="small">{model.model_name}</span>
                              <span className="badge bg-info">{model.count} calls</span>
                            </div>
                          ))}
                        </div>
                        <div className="col-md-6">
                          <h6 className="text-muted mb-2">Top Users</h6>
                          {detailedData.llmBreakdown.user_breakdown.slice(0, 5).map((user) => (
                            <div key={user.user_id} className="d-flex justify-content-between align-items-center mb-2">
                              <span className="small">{user.name}</span>
                              <span className="badge bg-secondary">{user.count} calls</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {detailedData.llm.data.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-sm">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Calls</th>
                              <th>Tokens</th>
                              <th>Avg Duration</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detailedData.llm.data.map((day) => (
                              <tr key={day.date}>
                                <td>{formatDate(day.date)}</td>
                                <td>
                                  <button
                                    className="btn btn-link p-0"
                                    onClick={() => setActiveLLMBreakdown({
                                      date: day.date,
                                      user_breakdown: day.user_breakdown || [],
                                      total_calls: day.count
                                    })}
                                    style={{ textDecoration: 'none' }}
                                  >
                                    <span className="badge bg-info">{day.count}</span>
                                  </button>
                                </td>
                                <td className="small">{day.total_tokens.toLocaleString()}</td>
                                <td className="small">
                                  {day.count > 0 ? Math.round(day.total_duration_ms / day.count) : 0}ms
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-muted">No LLM calls in the selected time period.</p>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-3">
                    <div className="spinner-border spinner-border-sm" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Login Analytics Section */}
        <div className="accordion-item">
          <h2 className="accordion-header">
            <button
              className={`accordion-button ${expandedSections.logins ? '' : 'collapsed'}`}
              type="button"
              onClick={() => toggleSection('logins')}
            >
              <TrendingUp size={20} className="me-2" />
              Login Analytics
            </button>
          </h2>
          {expandedSections.logins && (
            <div className="accordion-collapse collapse show">
              <div className="accordion-body">
                {detailedData.logins ? (
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">Daily User Logins ({days} days)</h6>
                      <small className="text-muted">
                        Total: {detailedData.logins.total_logins} logins | {detailedData.logins.unique_users_total} unique users
                      </small>
                    </div>

                    {/* Device Breakdown */}
                    {detailedData.logins.device_breakdown && (
                      <div className="row mb-4">
                        <div className="col-md-12">
                          <h6 className="text-muted mb-2">Device Breakdown</h6>
                          <div className="d-flex gap-3">
                            <div className="d-flex align-items-center">
                              <span className="badge bg-primary me-2">{detailedData.logins.device_breakdown.desktop}</span>
                              <span className="small">Desktop</span>
                            </div>
                            <div className="d-flex align-items-center">
                              <span className="badge bg-success me-2">{detailedData.logins.device_breakdown.mobile}</span>
                              <span className="small">Mobile</span>
                            </div>
                            <div className="d-flex align-items-center">
                              <span className="badge bg-secondary me-2">{detailedData.logins.device_breakdown.unknown}</span>
                              <span className="small">Unknown</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {detailedData.logins.data.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-sm">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Total Logins</th>
                              <th>Unique Users</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detailedData.logins.data.map((day) => (
                              <tr key={day.date}>
                                <td>{formatDate(day.date)}</td>
                                <td>
                                  <span className="badge bg-primary">{day.count}</span>
                                </td>
                                <td>
                                  <span className="badge bg-info">{day.unique_users_count}</span>
                                </td>
                                <td>
                                  {day.logins.length > 0 && (
                                    <button
                                      className="btn btn-sm btn-outline-secondary"
                                      onClick={() => setActiveDetailView({
                                        type: 'logins',
                                        date: day.date,
                                        data: day.logins
                                      })}
                                    >
                                      <Eye size={14} className="me-1" />
                                      View Details
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-muted">No logins in the selected time period.</p>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-3">
                    <div className="spinner-border spinner-border-sm" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Detail View Modal */}
      {activeDetailView && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {activeDetailView.type === 'recruiters' && 'Recruiter Details'}
                  {activeDetailView.type === 'jobseekers' && 'Jobseeker Details'}
                  {activeDetailView.type === 'jobs' && 'Job Posting Details'}
                  {activeDetailView.type === 'logins' && 'Login Details'}
                  {' - '}{formatDate(activeDetailView.date)}
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setActiveDetailView(null)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        {activeDetailView.type === 'recruiters' && (
                          <>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Company</th>
                            <th>Industry</th>
                            <th>Location</th>
                          </>
                        )}
                        {activeDetailView.type === 'jobseekers' && (
                          <>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Experience</th>
                            <th>Location</th>
                            <th>Skills</th>
                            <th>Added by Recruiter</th>
                          </>
                        )}
                        {activeDetailView.type === 'jobs' && (
                          <>
                            <th>Job Title</th>
                            <th>Company</th>
                            <th>Location</th>
                            <th>Recruiter</th>
                            <th>Status</th>
                          </>
                        )}
                        {activeDetailView.type === 'logins' && (
                          <>
                            <th>User</th>
                            <th>Role</th>
                            <th>Email</th>
                            <th>Time</th>
                            <th>IP Address</th>
                            <th>Device</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {activeDetailView.data.map((item, index) => (
                        <tr key={index}>
                          {activeDetailView.type === 'recruiters' && (
                            <>
                              <td>{item.name}</td>
                              <td className="small text-muted">{item.email}</td>
                              <td>{item.company}</td>
                              <td className="small">{item.industry}</td>
                              <td className="small">{item.location}</td>
                            </>
                          )}
                          {activeDetailView.type === 'jobseekers' && (
                            <>
                              <td>{item.name}</td>
                              <td className="small text-muted">{item.email}</td>
                              <td>{item.experience_years} years</td>
                              <td className="small">{item.location}</td>
                              <td className="small">
                                {item.skills ? item.skills.slice(0, 3).join(', ') : 'N/A'}
                                {item.skills && item.skills.length > 3 && '...'}
                              </td>
                              <td className="small text-muted">
                                {item.added_by_recruiter ? item.added_by_recruiter.email : 'Unknown'}
                              </td>
                            </>
                          )}
                          {activeDetailView.type === 'jobs' && (
                            <>
                              <td>{item.title}</td>
                              <td>{item.company}</td>
                              <td className="small">{item.location}</td>
                              <td className="small">{item.recruiter_name}</td>
                              <td>
                                <span className={`badge ${item.status === 'active' ? 'bg-success' : 'bg-secondary'}`}>
                                  {item.status}
                                </span>
                              </td>
                            </>
                          )}
                          {activeDetailView.type === 'logins' && (
                            <>
                              <td>{item.name}</td>
                              <td>
                                <span className={`badge ${
                                  item.role === 'recruiter' ? 'bg-primary' : 
                                  item.role === 'jobseeker' ? 'bg-success' : 
                                  item.role === 'admin' ? 'bg-danger' : 'bg-secondary'
                                }`}>
                                  {item.role}
                                </span>
                              </td>
                              <td className="small text-muted">{item.email}</td>
                              <td className="small">{new Date(item.timestamp).toLocaleTimeString()}</td>
                              <td className="small">{item.ip_address}</td>
                              <td className="small">
                                {item.location?.device_type || 'Unknown'}
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setActiveDetailView(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* LLM User Breakdown Modal */}
      {activeLLMBreakdown && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  LLM Calls Breakdown - {formatDate(activeLLMBreakdown.date)}
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setActiveLLMBreakdown(null)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <small className="text-muted">
                    Total calls on this date: <strong>{activeLLMBreakdown.total_calls}</strong>
                  </small>
                </div>
                
                {activeLLMBreakdown.user_breakdown.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>User</th>
                          <th>Role</th>
                          <th>Email</th>
                          <th>Calls</th>
                          <th>Tokens</th>
                          <th>Models Used</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activeLLMBreakdown.user_breakdown.map((user, index) => (
                          <tr key={user.user_id || index}>
                            <td>{user.name}</td>
                            <td>
                              <span className={`badge ${
                                user.role === 'recruiter' ? 'bg-primary' : 
                                user.role === 'jobseeker' ? 'bg-success' : 
                                user.role === 'admin' ? 'bg-danger' : 'bg-secondary'
                              }`}>
                                {user.role}
                              </span>
                            </td>
                            <td className="small text-muted">{user.email}</td>
                            <td>
                              <span className="badge bg-info">{user.count}</span>
                            </td>
                            <td className="small">{user.total_tokens.toLocaleString()}</td>
                            <td className="small">
                              {user.models_used && Object.keys(user.models_used).length > 0 ? (
                                <div>
                                  {Object.entries(user.models_used).map(([model, count]) => (
                                    <div key={model} className="mb-1">
                                      <span className="badge bg-light text-dark me-1">{model}</span>
                                      <small>({count})</small>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                'N/A'
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-muted">No user data available for this date.</p>
                )}
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setActiveLLMBreakdown(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel; 